import { useState, useCallback } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { mdiArrowRight, mdiClose, mdiLoading, mdiPlay, mdiPlayCircleOutline, mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'

interface NormalizationTestPanelProps {
  org: string
  uid: string
  testValues: string[]
  onTestValuesChange: (values: string[]) => void
}

interface TestResult {
  input: any
  output: any
  status: string
  trace: any[]
}

export function NormalizationTestPanel({ org, uid, testValues, onTestValuesChange }: NormalizationTestPanelProps) {
  const [inputValue, setInputValue] = useState('')
  const [newTestValue, setNewTestValue] = useState('')
  const [result, setResult] = useState<TestResult | null>(null)
  const [allResults, setAllResults] = useState<TestResult[] | null>(null)
  // Running a test is a read; STORING a test value PATCHes the normalization,
  // so only that half needs `configuration.write`.
  const canWriteConfiguration = useCan('configuration.write')

  const testMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/normalizations/{normalizationUid}/test' as any, {
    onSuccess: (data: any) => {
      if (data.results) {
        setAllResults(data.results)
        setResult(null)
      } else {
        setResult(data)
        setAllResults(null)
      }
    }
  })

  const handleTest = useCallback(() => {
    testMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
      body: { input_value: inputValue },
    } as any)
  }, [inputValue, org, uid, testMutation])

  const handleTestAll = useCallback(() => {
    testMutation.mutate({
      params: { path: { orgCode: org, normalizationUid: uid } },
      body: { test_all: true },
    } as any)
  }, [org, uid, testMutation])

  const handleAddTestValue = useCallback(() => {
    if (newTestValue && !testValues.includes(newTestValue)) {
      onTestValuesChange([...testValues, newTestValue])
      setNewTestValue('')
    }
  }, [newTestValue, testValues, onTestValuesChange])

  const handleRemoveTestValue = useCallback((index: number) => {
    onTestValuesChange(testValues.filter((_, i) => i !== index))
  }, [testValues, onTestValuesChange])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'rejected': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'invalid': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default: return ''
    }
  }

  const getTraceStepColor = (input: string, output: string, status?: string) => {
    if (status === 'rejected' || status === 'invalid') return 'bg-red-50/50 dark:bg-red-950/10'
    if (input === output) return 'bg-amber-50/50 dark:bg-amber-950/10'
    return 'bg-green-50/50 dark:bg-green-950/10'
  }

  const renderSingleResult = (r: TestResult) => (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge className={getStatusColor(r.status)} data-testid="normalization-test-status">
              {r.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Output:</span>
            <code className="bg-muted px-2 py-0.5 rounded text-sm font-mono" data-testid="normalization-test-output">
              {r.output === null ? 'null' : String(r.output)}
            </code>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <code className="bg-muted px-2 py-0.5 rounded font-mono">{String(r.input)}</code>
          <AppIcon path={mdiArrowRight} size="xs" />
          <code className="bg-muted px-2 py-0.5 rounded font-mono">{r.output === null ? 'null' : String(r.output)}</code>
        </div>
      </div>

      {/* Trace */}
      {r.trace && r.trace.length > 0 && (
        <div className="bg-card rounded-lg border border-border">
          <div className="px-5 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Pipeline Trace</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead className="w-32">Operation</TableHead>
                <TableHead>Input</TableHead>
                <TableHead>Output</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.trace.map((step: any, idx: number) => (
                <TableRow
                  key={idx}
                  className={getTraceStepColor(String(step.input), String(step.output), step.status)}
                >
                  <TableCell className="text-muted-foreground font-mono text-xs">{idx + 1}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">{step.op}</Badge>
                  </TableCell>
                  <TableCell>
                    <code className="text-sm font-mono">{String(step.input)}</code>
                  </TableCell>
                  <TableCell>
                    <code className="text-sm font-mono">{step.output === null ? 'null' : String(step.output)}</code>
                    {step.matched_key && (
                      <span className="text-xs text-muted-foreground ml-2">(matched: {step.matched_key})</span>
                    )}
                    {step.error && (
                      <div className="text-xs text-destructive mt-1">{step.error}</div>
                    )}
                    {step.status && step.status !== 'ok' && (
                      <Badge className={`ml-2 text-xs ${getStatusColor(step.status)}`}>{step.status}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Single value test */}
      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Test Single Value</h3>
        <div className="flex gap-3">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter a value to normalize..."
            className="font-mono"
            data-testid="normalization-test-input"
            onKeyDown={(e) => { if (e.key === 'Enter') handleTest() }}
          />
          <Button
            onClick={handleTest}
            disabled={testMutation.isPending || !inputValue}
            data-testid="normalization-test-btn"
          >
            {testMutation.isPending ? (
              <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
            ) : (
              <AppIcon path={mdiPlay} size="sm" className="mr-2" />
            )}
            Test
          </Button>
        </div>
      </div>

      {/* Stored test values */}
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Stored Test Values</h3>
          <Button
            onClick={handleTestAll}
            disabled={testMutation.isPending || testValues.length === 0}
            size="sm"
            data-testid="normalization-test-all-btn"
          >
            {testMutation.isPending ? (
              <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />
            ) : (
              <AppIcon path={mdiPlayCircleOutline} size="sm" className="mr-2" />
            )}
            Test All ({testValues.length})
          </Button>
        </div>

        {testValues.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {testValues.map((tv, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="gap-1 font-mono text-xs cursor-pointer hover:bg-accent"
                onClick={() => {
                  setInputValue(tv)
                  testMutation.mutate({
                    params: { path: { orgCode: org, normalizationUid: uid } },
                    body: { input_value: tv },
                  } as any)
                }}
                data-testid={`load-test-value-${idx}`}
              >
                {tv}
                {canWriteConfiguration && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveTestValue(idx) }}
                    className="ml-1 hover:text-destructive"
                    data-testid={`remove-test-value-${idx}`}
                  >
                    <AppIcon path={mdiClose} size="xs" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}

        {canWriteConfiguration && (
          <div className="flex gap-2">
            <Input
              value={newTestValue}
              onChange={(e) => setNewTestValue(e.target.value)}
              placeholder="Add a test value..."
              className="font-mono"
              data-testid="normalization-add-test-value-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTestValue() }}
            />
            <Button
              onClick={handleAddTestValue}
              disabled={!newTestValue}
              variant="outline"
              size="icon"
              data-testid="normalization-add-test-value-btn"
            >
              <AppIcon path={mdiPlus} size="sm" />
            </Button>
          </div>
        )}
      </div>

      {/* Single test result */}
      {result && renderSingleResult(result)}

      {/* All test results */}
      {allResults && allResults.length > 0 && (
        <div className="bg-card rounded-lg border border-border">
          <div className="px-5 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Test Results ({allResults.length} values)
            </h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Input</TableHead>
                <TableHead>Output</TableHead>
                <TableHead className="w-24">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allResults.map((r, idx) => (
                <TableRow
                  key={idx}
                  className={r.status !== 'ok' ? 'bg-red-50/50 dark:bg-red-950/10' : ''}
                >
                  <TableCell>
                    <code className="text-sm font-mono">{String(r.input)}</code>
                  </TableCell>
                  <TableCell>
                    <code className="text-sm font-mono">{r.output === null ? 'null' : String(r.output)}</code>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(r.status)} data-testid={`test-all-status-${idx}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
